import React, { useCallback, useEffect, useRef, useState } from 'react';

const KEYBOARDS = Object.freeze({
  greek: {
    label: 'Greek',
    language: 'grc',
    direction: 'ltr',
    rows: [
      Array.from('αβγδεζηθικλμνξοπρσςτυφχψω'),
      Array.from('ΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΣΤΥΦΧΨΩ'),
      ['ά', 'έ', 'ή', 'ί', 'ό', 'ύ', 'ώ', 'ἀ', 'ἁ', 'ἐ', 'ἑ', 'ἰ', 'ἱ', 'ὀ', 'ὁ', 'ὐ', 'ὑ', 'ὠ', 'ὡ'],
      [
        { value: '\u0301', label: '◌́', title: 'Acute' },
        { value: '\u0300', label: '◌̀', title: 'Grave' },
        { value: '\u0342', label: '◌͂', title: 'Circumflex' },
        { value: '\u0313', label: '◌̓', title: 'Smooth breathing' },
        { value: '\u0314', label: '◌̔', title: 'Rough breathing' },
        { value: '\u0308', label: '◌̈', title: 'Diaeresis' },
        { value: '\u0345', label: '◌ͅ', title: 'Iota subscript' },
        '·', '·', ';', '—', '«', '»',
      ],
    ],
  },
  hebrew: {
    label: 'Hebrew',
    language: 'he',
    direction: 'rtl',
    rows: [
      Array.from('אבגדהוזחטיכלמנסעפצקרשת'),
      Array.from('ךםןףץ'),
      [
        { value: '\u05B0', label: '◌ְ', title: 'Sheva' },
        { value: '\u05B1', label: '◌ֱ', title: 'Hatef segol' },
        { value: '\u05B2', label: '◌ֲ', title: 'Hatef patah' },
        { value: '\u05B3', label: '◌ֳ', title: 'Hatef qamats' },
        { value: '\u05B4', label: '◌ִ', title: 'Hiriq' },
        { value: '\u05B5', label: '◌ֵ', title: 'Tsere' },
        { value: '\u05B6', label: '◌ֶ', title: 'Segol' },
        { value: '\u05B7', label: '◌ַ', title: 'Patah' },
        { value: '\u05B8', label: '◌ָ', title: 'Qamats' },
        { value: '\u05B9', label: '◌ֹ', title: 'Holam' },
        { value: '\u05BB', label: '◌ֻ', title: 'Qubuts' },
        { value: '\u05BC', label: '◌ּ', title: 'Dagesh' },
        { value: '\u05BD', label: '◌ֽ', title: 'Meteg' },
        { value: '\u05C1', label: '◌ׁ', title: 'Shin dot' },
        { value: '\u05C2', label: '◌ׂ', title: 'Sin dot' },
        '־', '׃', '׀',
      ],
    ],
  },
});

const normalizedKey = (key) => typeof key === 'string'
  ? { value: key, label: key, title: key }
  : key;

const clamp = (value, minimum, maximum) =>
  Math.min(Math.max(Number(value) || 0, minimum), maximum);

const SeminaryLanguageKeyboard = ({
  language = 'greek',
  onLanguageChange,
  onInsert,
  onBackspace,
  onClose,
  disabled = false,
}) => {
  const active = KEYBOARDS[language] || KEYBOARDS.greek;
  const keyboardRef = useRef(null);
  const dragRef = useRef(null);
  const [position, setPosition] = useState(null);

  const constrainPosition = useCallback((left, top) => {
    const keyboard = keyboardRef.current;
    const width = keyboard?.offsetWidth || Math.min(920, window.innerWidth * 0.96);
    const height = keyboard?.offsetHeight || Math.min(520, window.innerHeight * 0.68);
    const controlsClearance = 92;
    return {
      left: clamp(left, 8, Math.max(8, window.innerWidth - width - 8)),
      top: clamp(
        top,
        8,
        Math.max(8, window.innerHeight - controlsClearance - height - 8)
      ),
    };
  }, []);

  const beginDrag = (event) => {
    if (event.target?.closest?.('button') || !keyboardRef.current) return;
    const rect = keyboardRef.current.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    setPosition(constrainPosition(rect.left, rect.top));
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch {}
    event.preventDefault();
  };

  const moveDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setPosition(constrainPosition(
      event.clientX - drag.offsetX,
      event.clientY - drag.offsetY
    ));
    event.preventDefault();
  };

  const endDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    try { event.currentTarget.releasePointerCapture?.(event.pointerId); } catch {}
  };

  useEffect(() => {
    const constrain = () => setPosition((current) => current
      ? constrainPosition(current.left, current.top)
      : current);
    window.addEventListener('resize', constrain);
    window.addEventListener('orientationchange', constrain);
    return () => {
      window.removeEventListener('resize', constrain);
      window.removeEventListener('orientationchange', constrain);
    };
  }, [constrainPosition]);

  return (
    <section
      ref={keyboardRef}
      className="fixed z-[125] max-h-[min(68vh,520px)] w-[min(96vw,920px)] overflow-y-auto rounded-xl border border-amber-400/35 bg-slate-950/95 p-2 shadow-2xl"
      aria-label={`${active.label} on-screen keyboard`}
      data-seminary-keyboard={language}
      data-floating-seminary-keyboard="true"
      style={position ? {
        left: position.left,
        top: position.top,
      } : {
        right: 12,
        bottom: 'calc(var(--meeting-controls-height, 5.75rem) + 0.75rem)',
      }}
    >
      <header
        className="mb-2 flex touch-none cursor-move items-center gap-2"
        aria-label={`Move ${active.label} on-screen keyboard`}
        onPointerDown={beginDrag}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={endDrag}
      >
        <strong className="min-w-0 flex-1 text-xs uppercase tracking-wider text-amber-200">
          Original-language keyboard
        </strong>
        {Object.entries(KEYBOARDS).map(([id, keyboard]) => (
          <button
            key={id}
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onLanguageChange?.(id)}
            className={`rounded px-2 py-1 text-xs ${language === id ? 'bg-amber-500 text-slate-950' : 'bg-slate-700 text-white'}`}
          >
            {keyboard.label}
          </button>
        ))}
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={onClose} className="rounded bg-slate-700 px-2 py-1 text-xs">
          Close
        </button>
      </header>
      <div dir={active.direction} lang={active.language} className="space-y-1">
        {active.rows.map((row, rowIndex) => (
          <div key={rowIndex} className="flex flex-wrap justify-center gap-1">
            {row.map((rawKey, keyIndex) => {
              const key = normalizedKey(rawKey);
              return (
                <button
                  key={`${key.value}-${keyIndex}`}
                  type="button"
                  disabled={disabled}
                  title={key.title}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => onInsert?.(key.value)}
                  className="min-h-9 min-w-9 rounded border border-slate-600 bg-slate-800 px-2 text-lg text-white hover:border-amber-300 hover:bg-slate-700 disabled:opacity-40"
                >
                  {key.label}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <footer className="mt-2 grid grid-cols-[1fr_2fr_1fr] gap-1" dir="ltr">
        <button type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={onBackspace} className="rounded bg-slate-700 px-2 py-2 text-xs disabled:opacity-40">
          Backspace
        </button>
        <button type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => onInsert?.(' ')} className="rounded bg-slate-700 px-2 py-2 text-xs disabled:opacity-40">
          Space
        </button>
        <button type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => onInsert?.('\n')} className="rounded bg-slate-700 px-2 py-2 text-xs disabled:opacity-40">
          Enter
        </button>
      </footer>
    </section>
  );
};

export default SeminaryLanguageKeyboard;
